import { Button as DDSButton } from '@driftt/dds'
import { faBullhorn } from '@driftt/dds.icons/light/faBullhorn'
import {
  Button,
  Div,
  H5,
  IconButton,
  Nav,
  Toggle,
  ToolTip,
} from '@driftt/tide-core'
import { Icon } from '@driftt/tide-icons'
import { Select } from '@driftt/tide-selects'
import PropTypes from 'prop-types'
import { compose, isEmpty, path, pathOr } from 'ramda'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { withRouter } from 'react-router-dom'

import { fetchTestData, setUrl } from 'api/flow'
import { exportPlaybookCopy } from 'api/playbooks'
import PlaybookErrorsNotifier from 'components/PlaybookErrorsNotifier'
import { META_TAGS } from 'constants/clpSettings'
import EditorSubNav from 'containers/EditorSubNav'
import AISparklesIcon from 'images/AISparklesIcon'
import { setBotFlowMode } from 'modules/editor/actions'
import { BOT_FLOW_MODES } from 'modules/editor/reducer'
import { pause } from 'utils'
import {
  getHasPlaybookExperiments,
  hasExportBotCopy,
  hasPlaybookContentViewMode,
  userHasAIPlaybooks,
} from 'utils/access'
import { getClpDomain } from 'utils/clp'
import createZipDownload from 'utils/createZipDownload'
import { isFastLanePlaybook, isMeetingBookingPlaybook } from 'utils/playbooks'
import {
  trackBotFlowPreview,
  trackBotFlowPreviewCLP,
  trackBotFlowPreviewExperiment,
  trackBotFlowShareClick,
  trackHowToBuildABotEduClicked,
} from 'utils/tracking'

import AIToggleModal from './AIToggleModal'
import BuildABotModal from './BuildABotModal'
import ExperimentModal from './ExperimentModal'
import FLInstallReminderModal from './FastLaneInstallReminderModal'
import FeedbackModal from './FeedbackModal'
import SharePlaybookModal from './SharePlaybookModal'
import TestBotDropdown from './TestBotDropdown'
import TestBotModal from './TestBotModal'
const BOT_FLOW_MODE_OPTIONS = [
  { value: BOT_FLOW_MODES.BUILD, label: 'Build mode' },
  { value: BOT_FLOW_MODES.CONTENT_VIEW, label: 'Content review mode' },
]

function SubNav({
  draftPlaybook,
  clpDomain,
  landingPageCname,
  landingPageCnameStatus,
  landingPageSettings,
  landingPageSubdomain,
  publishedPlaybook,
  isPlaybookDirty,
  isPreviewState,
  playbook,
  widgetInstalled,
  onCreateExperiment,
  onDeleteExperiment,
  onChangeAIEnabled,
  isAIEnabled,
  location,
  isBot,
  updateStatus,
  history,
}) {
  const canExportBotCopy = hasExportBotCopy()
  const hasContentView = hasPlaybookContentViewMode()
  const [isTestBotModalOpen, setIsTestBotModalOpen] = useState(false)
  const [isExperimentModalOpen, setIsExperimentModalOpen] = useState(false)
  const [isShareModalOpen, setIsShareModalOpen] = useState(false)
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false)
  const [isAIToggleModalOpen, setIsAIToggleModalOpen] = useState(false)
  const [isBuildABotModalOpen, setIsBuildABotModalOpen] = useState(false)

  const [testData, setTestData] = useState({
    slug: '',
    url: '',
  })
  const [experimentTestData, setExperimentTestData] = useState({
    slug: '',
    url: '',
  })

  const [canSaveUrl, setCanSaveUrl] = useState(false)
  const [isFastLaneInstallModalOpen, setIsFastLaneInstallModalOpen] =
    useState(null)

  const dispatch = useDispatch()

  const botFlowMode = useSelector(
    path(['__PLAYBOOK_EDITOR', 'editor', 'botFlowMode'])
  )

  const INTERACTION_ID = playbook?.actions[0]?.action?.interactionId

  const hasPlaybookExperiments = getHasPlaybookExperiments()
  const hasExperiment = !!draftPlaybook.experiment
  const isExperimentEnabled = !!draftPlaybook.experiment?.enabled
  const isExperimentEditorActive = location.pathname.endsWith(
    'visual-builder/experiment'
  )

  const activeTestData = isExperimentEditorActive
    ? experimentTestData
    : testData

  const isFastLane = isFastLanePlaybook(draftPlaybook)
  const isMeetingBooking = isMeetingBookingPlaybook(draftPlaybook)

  let experimentButtonText = 'Create A/B test'
  if (hasExperiment) {
    experimentButtonText = `Delete bot flow ${
      isExperimentEditorActive ? 'B' : 'A'
    }`
  }

  let testFlowButtonText = 'Preview bot flow'
  if (hasExperiment) {
    testFlowButtonText += isExperimentEditorActive ? ' B' : ' A'
  }

  useEffect(() => {
    async function getTestingData() {
      try {
        const query = {
          interactionId: INTERACTION_ID,
          includeExperiment: hasExperiment,
        }
        const { data } = await fetchTestData(query)
        setTestData({
          url: data.url ?? '',
          slug: data.testingSlug ?? '',
        })
        setExperimentTestData({
          url: data.experimentUrl ?? '',
          slug: data.experimentTestingSlug ?? '',
        })
        setCanSaveUrl(true)
      } catch (error) {
        console.warn(error.message)
      }
    }

    getTestingData()
  }, [INTERACTION_ID, hasExperiment])

  useEffect(() => {
    async function setTestingUrl() {
      try {
        const response = await setUrl({
          interactionId: INTERACTION_ID,
          url: activeTestData.url,
          isExperiment: isExperimentEditorActive,
        })
        if (response.status !== 204) {
          console.warn(`Response to setting url: ${response.status}`)
        }
      } catch (error) {
        console.warn(error.message)
      }
    }
    if (canSaveUrl) {
      setTestingUrl()
    }
  }, [
    INTERACTION_ID,
    activeTestData.url,
    canSaveUrl,
    hasExperiment,
    isExperimentEditorActive,
  ])

  async function downloadPlaybookCopyToZip() {
    try {
      await pause(250)
      const { data } = await exportPlaybookCopy(playbook?.id)
      createZipDownload(data)
    } catch (err) {
      console.warn(err)
    }
  }

  const hasUnpublishedExperiment = draftPlaybook.experiment
    ? !publishedPlaybook.experiment?.enabled
    : false

  const canTestChatBot =
    widgetInstalled &&
    !isEmpty(activeTestData.slug) &&
    !hasUnpublishedExperiment

  const getChatBotTestButtonToolTipMessage = () => {
    if (!widgetInstalled) {
      return (
        <>
          Install Drift on your site before testing a bot.
          <br />
          Go to the Drift Widget settings to update.
        </>
      )
    }
    if (hasUnpublishedExperiment) {
      return 'A/B test must be started.'
    }

    if (!isPreviewState && isPlaybookDirty) {
      return 'Testing shows the most recently published version.'
    }

    return ''
  }

  const handleClickTestButton = () => {
    setIsTestBotModalOpen(true)
    if (hasExperiment) {
      trackBotFlowPreviewExperiment(isExperimentEditorActive ? 'B' : 'A')
    } else {
      trackBotFlowPreview()
    }
  }

  const handleOpenExperimentModal = () => {
    setIsExperimentModalOpen(true)
  }

  const handleOpenShareModal = () => {
    trackBotFlowShareClick()
    setIsShareModalOpen(true)
  }

  const handleClickEdit = () => {
    setTestData({ ...testData, url: '' })
    setExperimentTestData({ ...experimentTestData, url: '' })
    setIsTestBotModalOpen(true)
  }

  const handleCloseModal = () => {
    setTestData({ ...testData, url: '' })
    setExperimentTestData({ ...experimentTestData, url: '' })
    setIsTestBotModalOpen(false)
  }

  const handleOnModeChange = ({ value }) => {
    dispatch(setBotFlowMode(value))
  }

  const handleToggleAI = () => {
    if (hasContentView) {
      return setIsAIToggleModalOpen(true)
    }
    isAIEnabled ? setIsAIToggleModalOpen(true) : onChangeAIEnabled()
  }

  // if the customer has targeted specific pages, pass those in to the modal
  const targetedSpecificUrls = useMemo(() => {
    let urls = []
    const groups = [...publishedPlaybook?.targeting?.display]
    if (groups) {
      groups.forEach((items) => {
        items.forEach((item) => {
          if (item.field === 'url' && item.operator === 'isAnyOf') {
            urls = [...urls, ...item.value]
          }
        })
      })
    }

    return urls
  }, [publishedPlaybook])

  function getLandingPageBotTestingLink() {
    const landingPageDomain = getClpDomain(
      clpDomain,
      landingPageCnameStatus,
      landingPageCname,
      landingPageSubdomain
    )

    return `https://${landingPageDomain}/${
      landingPageSettings.path ?? ''
    }?isTestingConversation=true`
  }

  const isLandingPageBot = () => {
    return pathOr(
      false,
      ['meta', META_TAGS.EMBEDDED_ONLY_PLAYBOOK],
      publishedPlaybook
    )
  }

  const onToggleAI = () => {
    if (hasContentView) {
      // reset to Build mode when toggling ON AI
      !isAIEnabled && handleOnModeChange({ value: BOT_FLOW_MODES.BUILD })
    }
    onChangeAIEnabled()
  }

  const isContentViewMode = botFlowMode === BOT_FLOW_MODES.CONTENT_VIEW
  const showUseAiTopics = userHasAIPlaybooks() && !isMeetingBooking
  const showBotBuilderVideo = !isMeetingBooking
  const showABTest =
    (hasPlaybookExperiments || hasExperiment) &&
    !isExperimentEnabled &&
    !isMeetingBooking
  const showDownload = (canExportBotCopy || isBot) && !isMeetingBooking

  return (
    <Nav className="playbook-editor-subnav">
      <Div className="playbook-editor-subnav-actions">
        {hasContentView && (
          <Select
            className="playbook-editor-subnav--mode-select"
            options={BOT_FLOW_MODE_OPTIONS}
            value={BOT_FLOW_MODE_OPTIONS.find(
              (option) => option.value === botFlowMode
            )}
            isSearchable={false}
            onChange={handleOnModeChange}
          />
        )}
        {showUseAiTopics && (
          <Div className="enable-ai-cta">
            <Toggle
              size="small"
              checked={isAIEnabled}
              onChange={handleToggleAI}
            />
            <AISparklesIcon />
            <H5>Use AI topics in this playbook.</H5>
          </Div>
        )}
        {isContentViewMode && (
          <DDSButton
            variant="education"
            onClick={() => setIsFeedbackModalOpen(true)}
          >
            <DDSButton.Icon icon={faBullhorn} />
            Give feedback
          </DDSButton>
        )}
      </Div>
      <Div className="playbook-editor-subnav-actions">
        <PlaybookErrorsNotifier isPreviewState={isPreviewState} />
        {showBotBuilderVideo && (
          <Div
            className="playbook-editor-subnav--bot-building"
            onClick={() => {
              setIsBuildABotModalOpen(true)
              trackHowToBuildABotEduClicked()
            }}
          >
            <Icon
              className="playbook-editor-subnav--bot-building-icon"
              name="video-play"
            />
            How to build a bot
          </Div>
        )}
        {!isFastLane && (
          <Div className="playbook-editor-subnav--bot-testing">
            {isLandingPageBot() ? (
              <ToolTip
                className="playbook-editor-subnav--bot-testing__tooltip"
                render={() => 'Your playbook must be turned on for testing'}
                hover={publishedPlaybook.status !== 'ENABLED'}
                flow="up"
              >
                <Button
                  href={getLandingPageBotTestingLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  type="tertiary"
                  disabled={publishedPlaybook.status !== 'ENABLED'}
                  onClick={() => trackBotFlowPreviewCLP()}
                >
                  Preview bot flow
                </Button>
              </ToolTip>
            ) : (
              <ToolTip
                component={getChatBotTestButtonToolTipMessage()}
                hover={!canTestChatBot || isPlaybookDirty}
                flow="up"
              >
                {!!testData.url ? (
                  <TestBotDropdown
                    onClickEdit={handleClickEdit}
                    shouldTestExperiment={isExperimentEditorActive}
                    testData={activeTestData}
                    isDisabled={!canTestChatBot}
                  />
                ) : (
                  <Button
                    onClick={handleClickTestButton}
                    type="tertiary"
                    disabled={!canTestChatBot}
                  >
                    {testFlowButtonText}
                  </Button>
                )}
              </ToolTip>
            )}
          </Div>
        )}
        {showABTest && (
          <ToolTip render={() => `A/B test "${playbook?.name}"`} flow="up">
            <Button
              className="playbook-editor-subnav--experiment"
              type="tertiary"
              onClick={handleOpenExperimentModal}
            >
              {experimentButtonText}
            </Button>
          </ToolTip>
        )}
        {hasContentView && (
          <ToolTip render={() => `Share "${playbook?.name}"`} flow="up">
            <Button
              className="playbook-editor-subnav--share"
              type="primary"
              onClick={handleOpenShareModal}
            >
              Share
            </Button>
          </ToolTip>
        )}
        {showDownload && (
          <ToolTip render={() => 'Download bot text'} flow="left">
            <IconButton
              className="playbook-editor-subnav--download"
              icon="download-2"
              onClick={downloadPlaybookCopyToZip}
              type="utility"
            />
          </ToolTip>
        )}
        <AIToggleModal
          isAIEnabled={isAIEnabled}
          open={isAIToggleModalOpen}
          onClose={() => setIsAIToggleModalOpen(false)}
          onToggleAI={onToggleAI}
        />
        <ExperimentModal
          onClose={() => setIsExperimentModalOpen(false)}
          onCreateExperiment={onCreateExperiment}
          onDeleteExperiment={onDeleteExperiment}
          hasExperiment={hasExperiment}
          isExperimentEditorActive={isExperimentEditorActive}
          open={isExperimentModalOpen}
        />
        <SharePlaybookModal
          onClose={() => setIsShareModalOpen(false)}
          open={isShareModalOpen}
          playbook={playbook}
        />
        <FeedbackModal
          onClose={() => setIsFeedbackModalOpen(false)}
          open={isFeedbackModalOpen}
        />
        {isFastLaneInstallModalOpen && (
          <FLInstallReminderModal
            id={publishedPlaybook.id}
            onClose={() => setIsFastLaneInstallModalOpen(false)}
            open={isFastLaneInstallModalOpen}
            status={publishedPlaybook.status}
            updateStatus={updateStatus}
          />
        )}
        <Div id="modal-root">
          <TestBotModal
            isOpen={isTestBotModalOpen}
            onClose={handleCloseModal}
            slug={activeTestData.slug}
            specificUrls={targetedSpecificUrls}
          />
        </Div>
        <BuildABotModal
          open={isBuildABotModalOpen}
          draftPlaybook={draftPlaybook}
          onClose={() => setIsBuildABotModalOpen(false)}
        />
      </Div>
    </Nav>
  )
}

SubNav.propTypes = {
  draftPlaybook: PropTypes.object.isRequired,
  showTransientNotification: PropTypes.func.isRequired,
  clpDomain: PropTypes.string,
  landingPageCname: PropTypes.string,
  landingPageCnameStatus: PropTypes.string,
  landingPageSettings: PropTypes.object,
  landingPageSubdomain: PropTypes.string,
  publishedPlaybook: PropTypes.object,
  autoSaving: PropTypes.bool,
  isPlaybookDirty: PropTypes.bool.isRequired,
  isPreviewState: PropTypes.bool.isRequired,
  playbook: PropTypes.object.isRequired,
  targeting: PropTypes.object,
  widgetInstalled: PropTypes.bool,
  onCreateExperiment: PropTypes.func.isRequired,
  onDeleteExperiment: PropTypes.func.isRequired,
}

SubNav.defaultTypes = {
  clpDomain: '',
  landingPageCname: '',
  landingPageCnameStatus: '',
  landingPageSettings: {},
  landingPageSubdomain: '',
  publishedPlaybook: {},
  autoSaving: false,
  targeting: {},
  widgetInstalled: false,
}

export default compose(EditorSubNav, withRouter)(SubNav)
